import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 *
 */
export type TDetailCustomer = {
  customerId: string;
  name: string;
  profilePicture?: string;
  email?: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  country?: string;
  gender?: string;
  interest?: string;
  notes?: string;
  labelCustomers: {
    labelCustomerId: string;
    name: string;
    color: string;
  }[];
  organizationChannel: {
    organizationChannelId: string;
    displayName: string;
    channel: {
      channelId: string;
      name: string;
    };
  };
  groupInfo?: {
    groupInfoId: string;
    name: string;
    picture: string;
  };
};
export const DetailCustomerSchema = z.object({
  customerId: z.string({ required_error: 'Customer ID is required!' }).uuid(),
});
export type TDetailCustomerQueryParams = z.infer<typeof DetailCustomerSchema>;
export type TDetailCustomerQueryResponse = ApiResponse<TDetailCustomer>;

const getDetailCustomer = async (params: TDetailCustomerQueryParams): Promise<TDetailCustomerQueryResponse> => {
  const url = `/customer-management/${params.customerId}`;

  return axiosInstance.get(url, {
    params,
  });
};

/**
 *
 */
export const GET_DETAIL_CUSTOMER_QUERY = 'detail_customer';

export const getDetailCustomerQueryOptions = <TData = TDetailCustomer>(
  params: TDetailCustomerQueryParams,
  select?: (data: TDetailCustomerQueryResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_DETAIL_CUSTOMER_QUERY,
      {
        customerId: params?.customerId || '',
      },
    ],
    queryFn: () => getDetailCustomer(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useDetailCustomerQuery<TData>(options: {
  params: TDetailCustomerQueryParams;
  queryConfig?: QueryConfig<typeof getDetailCustomerQueryOptions<TData>>;
  select: (data: TDetailCustomerQueryResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useDetailCustomerQuery(options: {
  params: TDetailCustomerQueryParams;
  queryConfig?: QueryConfig<typeof getDetailCustomerQueryOptions<TDetailCustomerQueryResponse>>;
}): ReturnType<typeof useQuery<TDetailCustomerQueryResponse>>;

// Implementation
export function useDetailCustomerQuery<TData = TDetailCustomerQueryResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TDetailCustomerQueryParams;
  queryConfig?: QueryConfig<typeof getDetailCustomerQueryOptions<TData | TDetailCustomerQueryResponse>>;
  select?: (data: TDetailCustomerQueryResponse) => TData;
}) {
  return useQuery({
    ...getDetailCustomerQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TDetailCustomerQueryResponse ? TDetailCustomerQueryResponse : TData>>;
}

// Backward compatibility - deprecated
/**
 * @deprecated Use useDetailCustomerQuery instead
 */
export const useDetailCustomer = useDetailCustomerQuery;
